import {
  findNaturalNextNode,
  findPlaybackNodeAtTime,
  resolveQueuedTarget,
  validPlaybackNodes,
  type ProjectConfig,
} from '../lib/project'

export const COARSE_INTERVAL_MS = 250
export const FINE_INTERVAL_MS = 50
export const FINE_PHASE_SECONDS = 2
export const PHASE_CORRECTION_MIN_LEAD_MS = COARSE_INTERVAL_MS * 2
export const QUEUE_LOCK_SECONDS = 1.5
export const MAX_LATENESS_MS = 150
export const LONG_TASK_SAMPLE_MS = 500

export type PlayerStatus =
  | 'unloaded'
  | 'cover'
  | 'ready'
  | 'playing'
  | 'buffering'
  | 'paused'
  | 'switching'
  | 'ended'
  | 'autoplay-blocked'
  | 'error'

export type TimingMode = {
  phaseCorrection: boolean
  deviceCompensation: boolean
}

export type PlayerSnapshot = {
  status: PlayerStatus
  actualTime: number
  currentNodeId: string | null
  queuedNodeId: string | null
  playbackIntent: boolean
}

export type BoundaryDecision =
  | { type: 'none' }
  | { type: 'queued'; targetNodeId: string; targetStart: number }
  | { type: 'natural'; nextNodeId: string }
  | { type: 'final-stop' }

export function createPlayerSnapshot(config: ProjectConfig): PlayerSnapshot {
  const first = validPlaybackNodes(config.nodes, config.video.duration)[0]
  return {
    status: 'unloaded',
    actualTime: first?.start ?? 0,
    currentNodeId: first?.id ?? null,
    queuedNodeId: null,
    playbackIntent: false,
  }
}

export function observeActualTime(
  snapshot: PlayerSnapshot,
  config: ProjectConfig,
  actualTime: number,
  preserveQueueWithinSegment = true,
): PlayerSnapshot {
  const observed = findPlaybackNodeAtTime(
    config.nodes,
    config.video.duration,
    actualTime,
    snapshot.currentNodeId,
  )
  const crossedSegment = observed?.id !== snapshot.currentNodeId
  return {
    ...snapshot,
    actualTime,
    currentNodeId: observed?.id ?? null,
    queuedNodeId: crossedSegment && !preserveQueueWithinSegment ? null : snapshot.queuedNodeId,
  }
}

export function toggleQueue(
  snapshot: PlayerSnapshot,
  config: ProjectConfig,
  targetNodeId: string,
  actualTime: number,
): { snapshot: PlayerSnapshot; locked: boolean } {
  const current = findPlaybackNodeAtTime(
    config.nodes,
    config.video.duration,
    actualTime,
    snapshot.currentNodeId,
  )
  if (!current || current.end - actualTime <= QUEUE_LOCK_SECONDS) return { snapshot, locked: true }
  const validTargets = new Set(
    config.edges
      .filter((edge) => edge.from === current.id)
      .map((edge) => edge.to),
  )
  if (!validTargets.has(targetNodeId)) return { snapshot: { ...snapshot, queuedNodeId: null }, locked: false }
  return {
    snapshot: {
      ...snapshot,
      actualTime,
      currentNodeId: current.id,
      queuedNodeId: snapshot.queuedNodeId === targetNodeId ? null : targetNodeId,
    },
    locked: false,
  }
}

export function decideBoundary(
  snapshot: PlayerSnapshot,
  config: ProjectConfig,
  actualTime: number,
): BoundaryDecision {
  const current = findPlaybackNodeAtTime(
    config.nodes,
    config.video.duration,
    Math.min(actualTime, Number.MAX_SAFE_INTEGER),
    snapshot.currentNodeId,
  )
    ?? (snapshot.currentNodeId
      ? validPlaybackNodes(config.nodes, config.video.duration).find((node) => node.id === snapshot.currentNodeId) ?? null
      : null)
  if (!current) return { type: 'none' }
  const gate = current.end - config.playbackSettings.endToleranceSeconds
  if (actualTime < gate) return { type: 'none' }

  const nodes = validPlaybackNodes(config.nodes, config.video.duration)
  const queuedId = resolveQueuedTarget(snapshot.queuedNodeId, new Set(nodes.map((node) => node.id)))
  if (queuedId) {
    const target = nodes.find((node) => node.id === queuedId)!
    return { type: 'queued', targetNodeId: target.id, targetStart: target.start }
  }
  const natural = findNaturalNextNode(config.nodes, config.video.duration, current.id)
  return natural ? { type: 'natural', nextNodeId: natural.id } : { type: 'final-stop' }
}

export class LatenessEstimator {
  private estimate = 0

  get valueMs() {
    return this.estimate
  }

  sample(latenessMs: number, valid = true) {
    if (!valid || latenessMs <= 0 || latenessMs > LONG_TASK_SAMPLE_MS) return this.estimate
    const sample = Math.min(MAX_LATENESS_MS, latenessMs)
    this.estimate = Math.min(MAX_LATENESS_MS, this.estimate === 0 ? sample : this.estimate * 0.8 + sample * 0.2)
    return this.estimate
  }

  reset() {
    this.estimate = 0
  }
}

export function physicalDeadline(logicalDeadlineMs: number, mode: TimingMode, estimatedLatenessMs: number) {
  return logicalDeadlineMs - (mode.phaseCorrection && mode.deviceCompensation
    ? Math.max(0, estimatedLatenessMs)
    : 0)
}

export function nextAbsoluteDeadline(
  phaseOriginMs: number,
  nowMs: number,
  intervalMs: number,
) {
  const elapsed = nowMs - phaseOriginMs
  const phase = elapsed / intervalMs
  const nearestGridIndex = Math.round(phase)
  const nextGridIndex = Math.abs(phase - nearestGridIndex) < 1e-9
    ? nearestGridIndex + 1
    : Math.floor(phase) + 1
  return phaseOriginMs + nextGridIndex * intervalMs
}

export function boundaryAlignedWakeSequence(
  boundarySeconds: number,
  afterSeconds: number,
  intervalSeconds: number,
  count: number,
) {
  const points: number[] = []
  let cursorMs = afterSeconds * 1000
  const boundaryMs = boundarySeconds * 1000
  const intervalMs = intervalSeconds * 1000
  for (let index = 0; index < count; index += 1) {
    const next = nextAbsoluteDeadline(boundaryMs, cursorMs, intervalMs)
    points.push(next / 1000)
    cursorMs = next
  }
  return points
}

type SchedulerOptions = {
  now: () => number
  getActualTime: () => number
  onSample: (actualTime: number) => void
  onBoundary: (actualTime: number) => boolean
  isSampleValid: () => boolean
  setTimer: (callback: () => void, delayMs: number) => number
  clearTimer: (id: number) => void
}

/**
 * Owns the player's sole active timer. Logical deadlines remain on one
 * absolute phase grid; physical registration may be moved earlier, but the
 * media-time boundary gate remains authoritative.
 */
export class AbsoluteWakeScheduler {
  private timerId: number | null = null
  private generation = 0
  private phaseOriginMs = 0
  private boundaryDeadlineMs: number | null = null
  private lastLogicalDeadlineMs = Number.NEGATIVE_INFINITY
  private boundarySeconds: number | null = null
  private queued = false
  private mode: TimingMode = { phaseCorrection: true, deviceCompensation: true }
  readonly lateness = new LatenessEstimator()

  constructor(private readonly options: SchedulerOptions) {}

  get activeTimerCount() {
    return this.timerId === null ? 0 : 1
  }

  get currentGeneration() {
    return this.generation
  }

  start(boundarySeconds: number | null, queued: boolean, mode: TimingMode) {
    this.stop(false)
    this.generation += 1
    this.phaseOriginMs = this.options.now()
    const actual = this.options.getActualTime()
    this.boundarySeconds = boundarySeconds
    this.boundaryDeadlineMs = boundarySeconds === null
      ? null
      : this.phaseOriginMs + Math.max(0, boundarySeconds - actual) * 1000
    this.lastLogicalDeadlineMs = Number.NEGATIVE_INFINITY
    this.queued = queued
    this.mode = queued
      ? { ...mode, deviceCompensation: mode.phaseCorrection && mode.deviceCompensation }
      : { phaseCorrection: false, deviceCompensation: false }
    this.schedule(this.generation)
  }

  get currentTimingMode() {
    return this.mode
  }

  get hasQueuedBoundary() {
    return this.queued
  }

  get estimatedBoundaryDeadlineMs() {
    return this.boundaryDeadlineMs
  }

  rebuild(boundarySeconds: number | null, queued: boolean, mode: TimingMode) {
    this.start(boundarySeconds, queued, mode)
  }

  stop(resetEstimate = false) {
    this.generation += 1
    if (this.timerId !== null) this.options.clearTimer(this.timerId)
    this.timerId = null
    if (resetEstimate) this.lateness.reset()
  }

  private schedule(generation: number) {
    if (generation !== this.generation) return
    const now = this.options.now()
    const actual = this.options.getActualTime()
    const remaining = this.boundarySeconds === null ? Number.POSITIVE_INFINITY : this.boundarySeconds - actual
    const interval = remaining <= FINE_PHASE_SECONDS ? FINE_INTERVAL_MS : COARSE_INTERVAL_MS
    const logical = this.queued && this.mode.phaseCorrection && this.boundaryDeadlineMs !== null
      ? nextAbsoluteDeadline(
        this.boundaryDeadlineMs,
        Math.max(now, this.lastLogicalDeadlineMs),
        interval,
      )
      : now + interval
    this.lastLogicalDeadlineMs = logical
    const physical = physicalDeadline(logical, this.mode, this.lateness.valueMs)
    this.timerId = this.options.setTimer(() => {
      this.timerId = null
      if (generation !== this.generation) return
      const wokeAt = this.options.now()
      this.lateness.sample(wokeAt - physical, this.options.isSampleValid())
      const sampledActual = this.options.getActualTime()
      this.options.onSample(sampledActual)
      if (this.options.onBoundary(sampledActual)) return
      if (
        this.queued
        && this.mode.phaseCorrection
        && this.boundarySeconds !== null
        && (this.boundarySeconds - sampledActual) * 1000 >= PHASE_CORRECTION_MIN_LEAD_MS
      ) {
        this.boundaryDeadlineMs = wokeAt + (this.boundarySeconds - sampledActual) * 1000
      }
      this.schedule(generation)
    }, Math.max(0, physical - now))
  }
}
